/**
 * EMETTE — Scroll Animations & UI Enhancements
 * - Intersection Observer reveal system
 * - Header transparency on scroll
 * - Parallax on hero
 * - Lottie icon loader
 * - Marquee clone duplication
 */

(function () {
  'use strict';

  // ── Scroll Reveal ──────────────────────────────────────────
  function initReveal() {
    const els = document.querySelectorAll('[data-em-reveal]');
    if (!els.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    els.forEach((el) => observer.observe(el));
  }

  // ── Hero Image Reveal ──────────────────────────────────────
  function initHeroReveal() {
    const heroImg = document.querySelector('.emette-hero__image');
    if (!heroImg) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(heroImg);
  }

  // ── Sticky Header Behavior ─────────────────────────────────
  function initHeader() {
    const header = document.querySelector('.shopify-section-group-header-group');
    if (!header) return;

    let lastY = 0;
    let ticking = false;

    function updateHeader() {
      const y = window.scrollY;
      if (y > 60) {
        header.classList.add('header--scrolled');
      } else {
        header.classList.remove('header--scrolled');
      }
      // Hide on scroll down, show on scroll up
      if (y > lastY + 4 && y > 200) {
        header.classList.add('header--hidden');
      } else if (y < lastY - 4) {
        header.classList.remove('header--hidden');
      }
      lastY = y;
      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateHeader);
        ticking = true;
      }
    }, { passive: true });
  }

  // ── Parallax on Hero ───────────────────────────────────────
  function initParallax() {
    const heroImg = document.querySelector('.emette-hero__image img');
    if (!heroImg || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const hero = heroImg.closest('.emette-hero');

    function onScroll() {
      const rect = hero.getBoundingClientRect();
      const visible = rect.bottom > 0 && rect.top < window.innerHeight;
      if (!visible) return;

      const progress = -rect.top / (rect.height + window.innerHeight);
      const offset = progress * 60;
      heroImg.style.transform = `translateY(${offset}px) scale(1)`;
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // ── Marquee Clone ─────────────────────────────────────────
  function initMarquee() {
    const track = document.querySelector('.emette-marquee__track');
    if (!track) return;

    // Clone content so loop appears seamless
    const clone = track.cloneNode(true);
    track.parentElement.appendChild(clone);
  }

  // ── Lottie Icons ──────────────────────────────────────────
  function initLottie() {
    const lottieEls = document.querySelectorAll('[data-lottie-src]');
    if (!lottieEls.length) return;

    function loadLottiePlayer(cb) {
      if (customElements.get('lottie-player')) { cb(); return; }
      const s = document.createElement('script');
      s.src = 'https://unpkg.com/@lottiefiles/lottie-player@2/dist/lottie-player.js';
      s.onload = cb;
      document.head.appendChild(s);
    }

    loadLottiePlayer(() => {
      lottieEls.forEach((el) => {
        const src = el.dataset.lottieSrc;
        const speed = el.dataset.lottieSpeed || '1';
        const player = document.createElement('lottie-player');
        player.setAttribute('src', src);
        player.setAttribute('background', 'transparent');
        player.setAttribute('speed', speed);
        player.setAttribute('loop', '');
        player.setAttribute('autoplay', '');
        player.style.width = el.dataset.lottieWidth || '28px';
        player.style.height = el.dataset.lottieHeight || '28px';
        el.replaceWith(player);
      });
    });
  }

  // ── Add-to-Cart Feedback ───────────────────────────────────
  function initCartFeedback() {
    document.addEventListener('cart:add', () => {
      const icon = document.querySelector('.header__icon--cart');
      if (!icon) return;
      icon.classList.add('cart-bump');
      setTimeout(() => icon.classList.remove('cart-bump'), 400);
    });
  }

  // ── Lazy-load section images ───────────────────────────────
  function initLazyImages() {
    if ('loading' in HTMLImageElement.prototype) return; // native support

    const imgs = document.querySelectorAll('img[loading="lazy"]');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) img.src = img.dataset.src;
          observer.unobserve(img);
        }
      });
    });

    imgs.forEach((img) => observer.observe(img));
  }

  // ── Product card quick actions (hover) ────────────────────
  function initCardHover() {
    const cards = document.querySelectorAll('.card-wrapper');
    cards.forEach((card) => {
      const actions = card.querySelector('.card__footer');
      if (!actions) return;

      card.addEventListener('mouseenter', () => {
        actions.style.opacity = '1';
        actions.style.transform = 'translateY(0)';
      });
      card.addEventListener('mouseleave', () => {
        actions.style.opacity = '';
        actions.style.transform = '';
      });
    });
  }

  // ── Hero Title Character Stagger ──────────────────────────
  function initHeroChars() {
    const title = document.querySelector('.emette-hero__title');
    if (!title) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Don't re-run if already processed
    if (title.querySelector('.em-char')) return;

    // Remove data-em-reveal so the char spans handle animation independently.
    // Without this the parent would stay opacity:0 (from [data-em-reveal] CSS)
    // and the chars inside would never be visible.
    title.removeAttribute('data-em-reveal');
    title.removeAttribute('data-em-delay');
    title.style.opacity = '1';
    title.style.transform = 'none';

    const html = title.innerHTML;
    // Split text nodes only, preserve em tags
    const fragment = document.createDocumentFragment();
    const temp = document.createElement('div');
    temp.innerHTML = html;

    let charIndex = 0;

    function processNode(node, target) {
      if (node.nodeType === 3) {
        // Text node — wrap each char
        const text = node.textContent;
        text.split('').forEach((ch) => {
          if (ch === ' ' || ch === '\n') {
            target.appendChild(document.createTextNode(' '));
            return;
          }
          const span = document.createElement('span');
          span.className = 'em-char';
          span.textContent = ch;
          span.style.animationDelay = (charIndex * 35 + 300) + 'ms';
          charIndex++;
          target.appendChild(span);
        });
      } else if (node.nodeName === 'EM') {
        // Preserve the em tag, but animate chars inside it
        const em = document.createElement('em');
        node.childNodes.forEach((child) => processNode(child, em));
        target.appendChild(em);
      } else if (node.nodeName === 'BR') {
        target.appendChild(document.createElement('br'));
      } else {
        target.appendChild(node.cloneNode(true));
      }
    }

    temp.childNodes.forEach((node) => processNode(node, fragment));
    title.innerHTML = '';
    title.appendChild(fragment);
  }

  // ── Cursor Spotlight ──────────────────────────────────────
  function initSpotlight() {
    // Skip on touch devices — no cursor
    if (window.matchMedia('(pointer: coarse)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const spotlight = document.createElement('div');
    spotlight.className = 'em-spotlight';
    document.body.appendChild(spotlight);

    let rafId = null;
    let cx = -600;
    let cy = -600;

    document.addEventListener('mousemove', (e) => {
      cx = e.clientX;
      cy = e.clientY;
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        spotlight.style.setProperty('--em-cursor-x', cx + 'px');
        spotlight.style.setProperty('--em-cursor-y', cy + 'px');
        rafId = null;
      });
    }, { passive: true });
  }

  // ── Card Color Dots ───────────────────────────────────────
  function initCardColors() {
    const BRAND_COLORS = ['#DCC7B0', '#C8A882', '#4B362B', '#2F241D'];
    const cards = document.querySelectorAll('.card-wrapper');

    cards.forEach((card) => {
      const info = card.querySelector('.card__information');
      if (!info) return;

      const row = document.createElement('div');
      row.className = 'em-card-colors';

      BRAND_COLORS.forEach((color) => {
        const dot = document.createElement('span');
        dot.className = 'em-color-dot';
        dot.style.background = color;
        dot.setAttribute('aria-hidden', 'true');
        row.appendChild(dot);
      });

      info.appendChild(row);
    });
  }

  // ── Init All ──────────────────────────────────────────────
  function init() {
    initReveal();
    initHeroReveal();
    initHeader();
    initMarquee();
    initLottie();
    initCartFeedback();
    initLazyImages();
    initCardHover();
    initSpotlight();
    initCardColors();

    // Parallax only on desktop
    if (window.innerWidth > 768) initParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Reinit on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', init);
})();
